/**
 * Maps the Twilio outbound-dial failure recorded on a humanTaskCalls row into
 * plain-English guidance for the ops operator running the phone dialer.
 *
 * The browser Voice SDK only ever reports a generic
 * `ConnectionError (31005): Error sent from gateway in HANGUP` when an outbound
 * <Dial> leg is rejected — it never says WHY. The real reason is persisted
 * server-side (twilioErrorCode / twilioErrorMessage / twilioStatus, written by
 * the /twilio/status-callback webhook) and returned by GET /calls/:id. This
 * turns that into something the operator can act on (e.g. a geographic
 * permission block) instead of the opaque 31005.
 *
 * Codes are Twilio Voice error codes (https://www.twilio.com/docs/api/errors).
 */

export interface TwilioDialErrorInfo {
  /** Short, operator-facing reason for the failure. */
  reason: string;
  /** True when the failure is a Twilio geographic/dialing-permission block. */
  isPermissionBlock: boolean;
}

const GEO_PERMISSION_CODES = new Set([
  13227, // Dial: number not routable / no international permission for destination
  13225, // Dial: calls to this destination are not allowed (geo permission)
  21215, // Account not authorized to call this geographic number
]);

const INVALID_NUMBER_CODES = new Set([
  13224, // Twilio does not support calling this number, or it is invalid
  13223, // Invalid phone number format in the To field
  21211, // Invalid 'To' phone number
]);

export function describeTwilioDialError(
  errorCode: number | null | undefined,
  errorMessage?: string | null,
  twilioStatus?: string | null,
): TwilioDialErrorInfo | null {
  if (errorCode == null) {
    if (twilioStatus === 'busy') {
      return { reason: 'The number was busy.', isPermissionBlock: false };
    }
    if (twilioStatus === 'no-answer') {
      return { reason: 'No answer.', isPermissionBlock: false };
    }
    if (twilioStatus === 'failed') {
      return {
        reason:
          'The call could not be connected. Please check the number and try again.',
        isPermissionBlock: false,
      };
    }
    return null;
  }

  if (GEO_PERMISSION_CODES.has(errorCode)) {
    return {
      reason:
        'This number is blocked by Twilio international-calling (Geographic ' +
        'Permissions) — an admin needs to enable calling for this country in ' +
        `the Twilio console. [Twilio ${errorCode}]`,
      isPermissionBlock: true,
    };
  }

  if (INVALID_NUMBER_CODES.has(errorCode)) {
    return {
      reason:
        "Twilio can't dial this number — it may be invalid or not in " +
        `international (+country code) format. [Twilio ${errorCode}]`,
      isPermissionBlock: false,
    };
  }

  const detail = errorMessage ? `: ${errorMessage}` : '';
  return {
    reason: `The call failed (Twilio ${errorCode})${detail}.`,
    isPermissionBlock: false,
  };
}
